import { User } from '@project44/ui-utils';
import {
  DtoBase,
  SharedModels,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import {
  IRolesEditorPolicy,
  TUnifiedRolesMap,
} from '../../../UnifiedRoles/domain/interfaces';
import {
  IUserDetailsRoute,
  IUserDetailsRoutePayload,
  TUserDetailsRouteState,
} from '../interfaces';
import { UnifiedRolesDomainAbstractions } from './../../../UnifiedRoles';

export class UserDetailsRoute implements IUserDetailsRoute {
  constructor(private readonly payload: IUserDetailsRoutePayload) {}

  public readonly getUserTenantSpecificRoles: IUserDetailsRoute['getUserTenantSpecificRoles'] =
    () => {
      return this.payload.composeUseApiRequestWrapper(
        this.payload.getUserTenantSpecificRoles,
      );
    };

  public readonly updateUserTenantSpecificRoles: IUserDetailsRoute['updateUserTenantSpecificRoles'] =
    () => {
      return this.payload.composeUseApiRequestWrapper(
        async ({ tenantSpecificRoles }) => {
          return this.payload.updateUserTenantSpecificRoles(
            this.payload.userRolesAdapter(tenantSpecificRoles),
          );
        },
      );
    };

  public readonly changeUserTenantSpecificAdminRolesType: IUserDetailsRoute['changeUserTenantSpecificAdminRolesType'] =
    ({ featureFlags }) => {
      return this.payload.composeUseApiRequestWrapper(
        async (tenantSpecificTenantAdminPolicy) => {
          await this.payload.changeUserTenantSpecificAdminRolesType({
            tenantSpecificTenantAdminPolicy,
            featureFlags,
          });
        },
      );
    };

  public readonly getUserTenantAgnosticRoles: IUserDetailsRoute['getUserTenantAgnosticRoles'] =
    () => {
      return this.payload.composeUseApiRequestWrapper(
        this.payload.getUserTenantAgnosticRoles,
      );
    };

  public readonly updateUserTenantAgnosticRoles: IUserDetailsRoute['updateUserTenantAgnosticRoles'] =
    () => {
      return this.payload.composeUseApiRequestWrapper(
        async ({ tenantAgnosticRoles }) => {
          return this.payload.updateUserTenantAgnosticRoles(
            this.payload.userRolesAdapter(tenantAgnosticRoles),
          );
        },
      );
    };

  public readonly getUserDetails: IUserDetailsRoute['getUserDetails'] = () => {
    return this.payload.composeUseApiRequestWrapper(async (args) => {
      const { user } = await this.payload.getUserDetails(args);

      return user;
    });
  };

  public readonly updateUserDetails: IUserDetailsRoute['updateUserDetails'] =
    () => {
      return this.payload.composeUseApiRequestWrapper(
        async ({ tenant, userDetails }) => {
          await this.payload.updateUserDetails(userDetails);

          const { user } = await this.payload.getUserDetails({
            tenant,
            userId: userDetails.lakeId,
            includeDeleted: false,
          });

          return user;
        },
      );
    };

  public readonly getUserRouteDetails: IUserDetailsRoute['getUserRouteDetails'] =
    () => {
      const [, getUserTenantSpecificRoles] = this.getUserTenantSpecificRoles();
      const [, getUserDetailsHandler] = this.getUserDetails();

      const [getUserRouteDetailsState, getUserRouteDetailsHandler] =
        this.payload.composeUseApiRequestWrapper(
          async (payload: {
            tenant: DtoBase<TenantModels.TenantViewModel>;
            user: DtoBase<UserModels.UserViewModel>;
            userPrincipal: User;
          }) => {
            const { userPrincipal, tenant, user } = payload;

            const getUserTenantAgnosticRoles = this.handleTenantAgnosticRoles({
              tenantAgnosticRolesHandler:
                this.payload.getUserTenantAgnosticRoles,
              userPrincipal,
            });

            const [
              userTenantSpecificRolesData,
              userTenantAgnosticRolesData,
              userDetailsData,
            ] = await Promise.allSettled([
              getUserTenantSpecificRoles(),
              getUserTenantAgnosticRoles(),
              getUserDetailsHandler({
                userId: user.source.getLakeId(),
                tenant,
                includeDeleted: false,
              }),
            ]);

            return this.composeNonNullableUserRouteDetails({
              userDetailsData,
              userTenantSpecificRolesData,
              userTenantAgnosticRolesData,
            });
          },
        );

      return [
        {
          ...getUserRouteDetailsState,
          data: this.composeUserRouteNonNullableInitialStateDetails(
            getUserRouteDetailsState.data,
          ),
        },
        getUserRouteDetailsHandler,
      ];
    };

  public readonly updateUserRouteDetails: IUserDetailsRoute['updateUserRouteDetails'] =
    () => {
      const updateTenantSpecificRoles = this.updateUserTenantSpecificRoles()[1];
      const updateTenantAgnosticRoles = this.updateUserTenantAgnosticRoles()[1];
      const updateDetails = this.updateUserDetails()[1];
      const getUserRouteDetails = this.getUserRouteDetails()[1];

      return this.payload.composeUseApiRequestWrapper(
        async ({
          tenantSpecificRoles,
          tenantAgnosticRoles,
          userDetails,
          user,
          tenant,
          userPrincipal,
        }) => {
          await Promise.allSettled([
            updateTenantSpecificRoles({ tenantSpecificRoles }),
            updateTenantAgnosticRoles({ tenantAgnosticRoles }),
            updateDetails({ userDetails, tenant }),
          ]);

          return getUserRouteDetails({
            user,
            tenant,
            userPrincipal,
          });
        },
      );
    };

  public readonly composeUserRouteNonNullableInitialStateDetails = (payload?: {
    userDetails?: DtoBase<UserModels.UserViewModel>;
    userTenantSpecificRoles?: TUnifiedRolesMap;
    userTenantAgnosticRoles?: TUnifiedRolesMap;
    userTenantSpecificRolesEditor?: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
    userTenantAgnosticRolesEditor?: UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy;
  }): TUserDetailsRouteState => {
    const {
      userDetails,
      userTenantSpecificRoles = new Map<
        string,
        SharedModels.UnifiedRoleViewModel | undefined
      >(),
      userTenantAgnosticRoles = new Map<
        string,
        SharedModels.UnifiedRoleViewModel | undefined
      >(),
      userTenantSpecificRolesEditor = this.payload.userRolesEditor,
      userTenantAgnosticRolesEditor = this.payload.userRolesEditor,
    } = payload ?? {};

    return {
      userDetails: this.payload.composeUserDetailsDto(
        userDetails?.toSerializedModel(),
      ),
      userTenantSpecificRoles,
      userTenantAgnosticRoles,
      userTenantSpecificRolesEditor,
      userTenantAgnosticRolesEditor,
    };
  };

  public readonly composeNonNullableUserRouteDetails = (payload: {
    userTenantSpecificRolesData: PromiseSettledResult<
      IRolesEditorPolicy | undefined
    >;
    userTenantAgnosticRolesData: PromiseSettledResult<
      IRolesEditorPolicy | undefined
    >;
    userDetailsData: PromiseSettledResult<
      DtoBase<UserModels.UserViewModel> | undefined
    >;
  }): TUserDetailsRouteState => {
    const {
      userTenantSpecificRolesData,
      userTenantAgnosticRolesData,
      userDetailsData,
    } = payload;

    const userTenantSpecificRoles =
      userTenantSpecificRolesData.status === 'fulfilled'
        ? userTenantSpecificRolesData.value?.composeResourceTenantSpecificRolesMap()
        : undefined;

    const userTenantAgnosticRoles =
      userTenantAgnosticRolesData.status === 'fulfilled'
        ? userTenantAgnosticRolesData.value?.composeResourceTenantAgnosticRolesMap()
        : undefined;

    const userDetails =
      userDetailsData.status === 'fulfilled'
        ? userDetailsData.value
        : undefined;

    const userTenantAgnosticRolesEditor =
      userTenantAgnosticRolesData.status === 'fulfilled'
        ? userTenantAgnosticRolesData.value
        : undefined;

    const userTenantSpecificRolesEditor =
      userTenantSpecificRolesData.status === 'fulfilled'
        ? userTenantSpecificRolesData.value
        : undefined;

    return this.composeUserRouteNonNullableInitialStateDetails({
      userDetails,
      userTenantAgnosticRoles,
      userTenantSpecificRoles,
      userTenantAgnosticRolesEditor,
      userTenantSpecificRolesEditor,
    });
  };

  public readonly handleTenantAgnosticRoles = (payload: {
    tenantAgnosticRolesHandler: IUserDetailsRoutePayload['getUserTenantAgnosticRoles'];
    userPrincipal: User;
  }): IUserDetailsRoutePayload['getUserTenantAgnosticRoles'] => {
    const { tenantAgnosticRolesHandler, userPrincipal } = payload;

    return userPrincipal.hasAnyOfPermissions([
      'system-admin:read:tenant-agnostic-user-role-assignments',
    ])
      ? tenantAgnosticRolesHandler
      : async () => {
          return Promise.resolve(this.payload.userRolesEditor);
        };
  };

  public static readonly compose = (
    payload: IUserDetailsRoutePayload,
  ): UserDetailsRoute => {
    return new UserDetailsRoute(payload);
  };
}
